import React, { useState, useCallback } from "react";

const API = process.env.REACT_APP_API_URL || "";

function formatBytes(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function fileIcon(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  const icons = {
    pdf: "\u{1F4D1}", pptx: "\u{1F4CA}", ppt: "\u{1F4CA}",
    xlsx: "\u{1F4C8}", xls: "\u{1F4C8}", csv: "\u{1F4C8}",
    doc: "\u{1F4DD}", docx: "\u{1F4DD}", txt: "\u{1F4DD}",
    zip: "\u{1F4E6}", png: "\u{1F5BC}", jpg: "\u{1F5BC}", jpeg: "\u{1F5BC}",
  };
  return icons[ext] || "\u{1F4CE}";
}

const FileBrowser = ({ token, onLogout }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/uploads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        onLogout();
        return;
      }
      if (!res.ok) throw new Error("Failed to load files");
      const data = await res.json();
      setFiles(data.uploads || []);
      setLoaded(true);
    } catch (err) {
      setError(err.message || "Could not load files");
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  const handleDownload = (file) => {
    // Open a direct GET URL — the browser handles the download natively
    const params = new URLSearchParams({
      path: file.gcs_path,
      token: token,
    });
    window.open(`${API}/api/download?${params.toString()}`, "_blank");
  };

  const filteredFiles = files.filter((f) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      f.filename.toLowerCase().includes(term) ||
      f.uploader.toLowerCase().includes(term)
    );
  });

  // Not yet opened — just show the button
  if (!loaded && !loading) {
    return (
      <div style={styles.container}>
        <button onClick={fetchFiles} style={styles.viewBtn}>
          View Uploaded Documents
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.panel}>
        <div style={styles.panelHeader}>
          <h3 style={styles.panelTitle}>Uploaded Documents</h3>
          <div style={styles.headerActions}>
            <button onClick={fetchFiles} style={styles.refreshBtn} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
            <button onClick={() => { setLoaded(false); setFiles([]); }} style={styles.closeBtn}>
              Close
            </button>
          </div>
        </div>

        {/* Search bar */}
        {files.length > 0 && (
          <input
            type="text"
            placeholder="Search by filename or uploader..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        )}

        {error && <p style={styles.error}>{error}</p>}

        {loading && <p style={styles.loadingText}>Loading files...</p>}

        {!loading && files.length === 0 && (
          <p style={styles.emptyText}>No uploaded documents found.</p>
        )}

        {!loading && files.length > 0 && filteredFiles.length === 0 && (
          <p style={styles.emptyText}>No files match your search.</p>
        )}

        {!loading && filteredFiles.length > 0 && (
          <>
            <p style={styles.countText}>
              {filteredFiles.length} document{filteredFiles.length !== 1 ? "s" : ""}
              {searchTerm.trim() ? ` matching "${searchTerm}"` : ""}
            </p>
            <div style={styles.fileList}>
              {filteredFiles.map((file, i) => (
                <div key={file.gcs_path || i} style={styles.fileRow}>
                  <span style={styles.fileIcon}>{fileIcon(file.filename)}</span>
                  <div style={styles.fileInfo}>
                    <span style={styles.fileName}>{file.filename}</span>
                    <span style={styles.fileMeta}>
                      {formatBytes(file.size_bytes)} · {file.uploader} ·{" "}
                      {new Date(file.uploaded_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDownload(file)}
                    style={styles.downloadBtn}
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    marginTop: 28,
  },
  viewBtn: {
    width: "100%",
    padding: "14px",
    fontSize: 15,
    fontWeight: 600,
    color: "#1a1a2e",
    background: "#fff",
    border: "1.5px solid #d1d5db",
    borderRadius: 10,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.2s",
  },
  panel: {
    background: "#fff",
    borderRadius: 12,
    padding: 20,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  panelTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 600,
    color: "#1a1a2e",
  },
  headerActions: {
    display: "flex",
    gap: 8,
  },
  refreshBtn: {
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    color: "#ff4f8b",
    background: "rgba(255,79,139,0.06)",
    border: "1.5px solid #ff4f8b",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  closeBtn: {
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    background: "transparent",
    border: "1.5px solid #d1d5db",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  searchInput: {
    width: "100%",
    padding: "10px 14px",
    fontSize: 13,
    border: "1.5px solid #e0e0e0",
    borderRadius: 8,
    outline: "none",
    fontFamily: "inherit",
    marginBottom: 12,
    boxSizing: "border-box",
  },
  error: {
    color: "#ef4444",
    fontSize: 13,
    textAlign: "center",
    margin: "12px 0",
  },
  loadingText: {
    color: "#9ca3af",
    fontSize: 13,
    textAlign: "center",
    margin: "20px 0",
  },
  emptyText: {
    color: "#9ca3af",
    fontSize: 13,
    textAlign: "center",
    margin: "20px 0",
  },
  countText: {
    margin: "0 0 12px",
    fontSize: 12,
    color: "#9ca3af",
  },
  fileList: {
    maxHeight: 400,
    overflowY: "auto",
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 0",
    borderBottom: "1px solid #f3f4f6",
  },
  fileIcon: {
    fontSize: 22,
    flexShrink: 0,
  },
  fileInfo: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  fileName: {
    fontSize: 14,
    color: "#1f2937",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileMeta: {
    fontSize: 11,
    color: "#9ca3af",
  },
  downloadBtn: {
    flexShrink: 0,
    padding: "7px 16px",
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    background: "#ff4f8b",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
  },
};

export default FileBrowser;
